import { useState, useRef, useEffect, useCallback } from 'react';
import { useAutoSave } from '@/hooks';
import { TrashIcon, Cog6ToothIcon, EllipsisVerticalIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useConfirmDialog } from '../../../hooks/useConfirmDialog';
import { ConfirmDialog } from '../../shared/UIComponents';
import disciplineConfig from '../../../services/disciplineConfigService';
import EditableField from '../../shared/EditableField';
import MultiSelectDropdown from '../../shared/MultiSelectDropdown';
import OffenceCard from '../OffenceCard';
import { useSchool } from '@/contexts/SchoolContext';

// Helper function to calculate end date based on start date and duration
const calculateEndDate = (startDate, duration, durationType) => {
  const start = new Date(startDate);
  const end = new Date(start);

  switch (durationType) {
    case 'day':
      end.setDate(start.getDate() + duration);
      break;
    case 'week':
      end.setDate(start.getDate() + (duration * 7));
      break;
    case 'month':
      end.setMonth(start.getMonth() + duration);
      break;
    case 'term':
      end.setMonth(start.getMonth() + (duration * 3));
      break;
    case 'semester':
      end.setMonth(start.getMonth() + (duration * 6));
      break;
    case 'year':
      end.setFullYear(start.getFullYear() + duration);
      break;
    default:
      end.setDate(start.getDate() + duration);
  }

  return end.toISOString().split('T')[0];
};

// Helper function to format date for display (consistent format)
const formatDateForDisplay = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const BehaviorContractForm = ({ rule = null, ruleId = null, categoryKey = null, onSave, onDelete, availableOffenses = [], availableStudents = [], isRecognitionMode = false }) => {
  const { confirmDialog, showConfirm } = useConfirmDialog();
  const { schoolId } = useSchool();
  const prevRuleId = useRef(rule?.id);

  // Initialize with proper defaults
  const [data, setData] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    const defaults = {
      title: isRecognitionMode ? 'Growth Goal' : 'Behavior Contract',
      targets: [], // Student IDs or grade names
      offenseTypes: [], // Offense IDs to avoid
      duration: 1,
      durationType: 'month',
      violationConsequence: 'suspension',
      violationConsequenceId: null,
      startDate: today,
      endDate: calculateEndDate(today, 1, 'month'),
      active: true
    };

    if (!rule) return defaults;

    return {
      title: rule.title || defaults.title,
      targets: rule.targets || defaults.targets,
      offenseTypes: rule.offenseTypes || defaults.offenseTypes,
      duration: rule.duration || defaults.duration,
      durationType: rule.durationType || defaults.durationType,
      violationConsequence: rule.violationConsequence || defaults.violationConsequence,
      violationConsequenceId: rule.violationConsequenceId || null,
      startDate: rule.startDate || defaults.startDate,
      endDate: rule.endDate || calculateEndDate(rule.startDate || defaults.startDate, rule.duration || defaults.duration, rule.durationType || defaults.durationType),
      active: rule.active !== undefined ? rule.active : defaults.active,
      ...(rule.id && { id: rule.id })
    };
  });

  const [showConfig, setShowConfig] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [showOffenseSelector, setShowOffenseSelector] = useState(false);
  const [consequenceOptions, setConsequenceOptions] = useState([]);
  const [timeframeOptions, setTimeframeOptions] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);

  const menuRef = useRef();
  const datePickerRef = useRef();
  const offenseSelectorRef = useRef();

  // Load consequence types from Firestore
  useEffect(() => {
    const loadConsequenceTypes = async () => {
      try {
        const types = await disciplineConfig.getConsequenceTypes(schoolId);
        setConsequenceOptions(types.map(c => ({
          value: c.value,
          label: c.singular || c.label,
          id: c.id
        })));
      } catch (error) {
        console.error('Error loading consequence types:', error);
      }
    };
    loadConsequenceTypes();
  }, [schoolId]);

  // Load timeframes from Firestore
  useEffect(() => {
    const loadTimeframes = async () => {
      try {
        const options = await disciplineConfig.getTimeframeOptions(schoolId);
        setTimeframeOptions(options);
      } catch (error) {
        console.error('Error loading timeframes:', error);
      }
    };
    loadTimeframes();
  }, [schoolId]);

  // Load grades from Firestore
  useEffect(() => {
    const loadGrades = async () => {
      try {
        const options = await disciplineConfig.getGradeSelectOptions(schoolId);
        setGradeOptions(options);
      } catch (error) {
        console.error('Error loading grades:', error);
      }
    };
    loadGrades();
  }, [schoolId]);

  // Update data when we get a different rule
  useEffect(() => {
    if (rule?.id && rule.id !== prevRuleId.current) {
      prevRuleId.current = rule.id;

      const startDate = rule.startDate || new Date().toISOString().split('T')[0];
      const duration = rule.duration || 30;
      const durationType = rule.durationType || 'days';

      setData({
        title: rule.title || (isRecognitionMode ? 'Growth Goal' : 'Behavior Contract'),
        targets: rule.targets || [],
        offenseTypes: rule.offenseTypes || [],
        duration,
        durationType,
        violationConsequence: rule.violationConsequence || 'suspension',
        violationConsequenceId: rule.violationConsequenceId || null,
        startDate,
        endDate: rule.endDate || calculateEndDate(startDate, duration, durationType),
        active: rule.active !== undefined ? rule.active : true,
        ...(rule.id && { id: rule.id })
      });
    }
  }, [rule]);

  // Close menu and offense selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
      if (offenseSelectorRef.current && !offenseSelectorRef.current.contains(event.target)) {
        setShowOffenseSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { save: debouncedSave } = useAutoSave(onSave, 100);

  const update = useCallback((updates) => {
    let newData;
    setData(current => {
      newData = { ...current, ...updates };

      // Recalculate end date if any date-related fields changed
      if (updates.startDate || updates.duration || updates.durationType) {
        const startDate = updates.startDate || current.startDate;
        const duration = updates.duration || current.duration;
        const durationType = updates.durationType || current.durationType;
        newData.endDate = calculateEndDate(startDate, duration, durationType);
      }

      // If updating violationConsequence, also update the corresponding ID field
      if (updates.violationConsequence) {
        const typeOption = consequenceOptions.find(opt => opt.value === updates.violationConsequence);
        if (typeOption) {
          newData.violationConsequenceId = typeOption.id;
        }
      }

      return newData;
    });

    const actualRuleId = ruleId || rule?.id || newData.id;
    debouncedSave(newData, categoryKey || 'contract', actualRuleId);
  }, [debouncedSave, categoryKey, ruleId, rule?.id, consequenceOptions]);

  // Sortable setup
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule?.id || 'temp' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  // Get display name for a target
  const getTargetDisplayName = (targetId) => {
    // Check if it's a grade
    if (targetId.startsWith('Grade ')) return targetId;

    // Find student
    const student = availableStudents.find(s => s.id === targetId);
    return student ? student.name : targetId;
  };

  // Get display text for targets
  const getTargetsText = () => {
    if (data.targets.length === 0) return 'All students';
    const names = data.targets.map(t => getTargetDisplayName(t));
    if (names.length <= 2) return names.join(', ');
    return `${names[0]}, ${names[1]} +${names.length - 2}`;
  };

  // Get selected offenses as objects
  const getSelectedOffenses = () => {
    return data.offenseTypes.map(id =>
      availableOffenses.find(o => o.id === id)
    ).filter(Boolean);
  };

  // Add offense to the list
  const addOffense = (offense) => {
    if (!data.offenseTypes.includes(offense.id)) {
      update({ offenseTypes: [...data.offenseTypes, offense.id] });
    }
    setShowOffenseSelector(false);
  };

  // Remove offense from the list
  const removeOffense = (offenseId) => {
    update({ offenseTypes: data.offenseTypes.filter(id => id !== offenseId) });
  };

  // Filter out already selected offenses
  const availableOffensesFiltered = availableOffenses.filter(
    offense => !data.offenseTypes.includes(offense.id)
  );

  // Combine students and grades for selection
  const targetOptions = [
    ...gradeOptions,
    ...availableStudents.map(student => ({ value: student.id, label: student.name }))
  ];


  // consequenceOptions, timeframeOptions, and gradeOptions are now loaded via useEffect above

  // Map timeframe options for duration type dropdown
  const DURATION_TYPE_OPTIONS = timeframeOptions.map(opt => ({
    value: opt.value,
    label: data.duration > 1 ? opt.label : opt.singular
  }));

  // Check if contract dates are currently active
  const isDateRangeActive = () => {
    const now = new Date();
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    return now >= start && now <= end;
  };

  // Get date status display
  const getDateStatus = () => {
    const now = new Date();
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);

    if (now < start) {
      const daysUntil = Math.ceil((start - now) / (1000 * 60 * 60 * 24));
      return { type: 'future', text: `Starts in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}` };
    } else if (now > end) {
      const daysAgo = Math.ceil((now - end) / (1000 * 60 * 60 * 24));
      return { type: 'past', text: `Ended ${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago` };
    }
    return { type: 'active', text: null };
  };

  const dateStatus = getDateStatus();
  const dateRangeActive = isDateRangeActive();

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-sm ${!isDragging ? 'hover:shadow-md' : ''} relative transition-shadow`}
    >
      <div className="flex items-center p-4">
        {/* Drag Handle */}
        <div
          className="mr-2 py-1 cursor-grab hover:bg-base-200 rounded focus:outline-none"
          {...attributes}
          {...listeners}
          tabIndex={-1}
        >
          <svg
            className="w-4 h-4 text-base-content/50"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <circle cx="7" cy="5" r="1.5" />
            <circle cx="7" cy="10" r="1.5" />
            <circle cx="7" cy="15" r="1.5" />
            <circle cx="13" cy="5" r="1.5" />
            <circle cx="13" cy="10" r="1.5" />
            <circle cx="13" cy="15" r="1.5" />
          </svg>
        </div>

        <div className="flex-1">
          {/* Editable Title */}
          <div className="mb-3 flex items-center gap-2">
            <input
              key={rule?.id || 'new'}
              type="text"
              defaultValue={data.title}
              onBlur={(e) => {
                const newTitle = e.target.value.trim() || (isRecognitionMode ? 'Growth Goal' : 'Behavior Contract');
                if (newTitle !== data.title) {
                  update({ title: newTitle });
                }
                setEditingTitle(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.target.blur();
                }
              }}
              className={`text-lg font-medium bg-transparent border-0 p-0 flex-1 outline-none cursor-pointer ${
                editingTitle ? 'text-black' : 'text-blue-600 hover:text-blue-700'
              }`}
              onFocus={() => setEditingTitle(true)}
            />
            {/* Date range status indicator */}
            {!dateRangeActive && dateStatus.text && (
              <span className="text-xs px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-700">
                {dateStatus.text}
              </span>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="text-base pr-4">
              <span
                className="font-medium text-blue-600 cursor-pointer hover:text-blue-700"
                onClick={() => setShowConfig(!showConfig)}
              >
                {getTargetsText()}
              </span>
              {' '}{isRecognitionMode ? 'should demonstrate:' : 'must avoid:'}
            </div>

            <div className="flex items-center gap-2">
              {/* Active toggle */}
              <input
                type="checkbox"
                checked={data.active}
                onChange={(e) => update({ active: e.target.checked })}
                className={`toggle toggle-sm ${data.active ? 'toggle-success' : ''}`}
              />

              {/* Three dots menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1.5 hover:bg-base-200 rounded"
                >
                  <EllipsisVerticalIcon className="w-4 h-4" />
                </button>

                <AnimatePresence>
                  {showMenu && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute right-0 mt-1 w-40 bg-white border border-base-200 rounded-lg shadow-lg z-10 overflow-hidden"
                    >
                      <button
                        onClick={() => {
                          setShowConfig(!showConfig);
                          setShowMenu(false);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-base-100 flex items-center gap-2"
                      >
                        <Cog6ToothIcon className="w-4 h-4" />
                        {showConfig ? 'Less options' : 'More options'}
                      </button>
                      {onDelete && (
                        <button
                          onClick={async () => {
                            setShowMenu(false);
                            const confirmed = await showConfirm({
                              title: 'Delete Rule?',
                              message: 'Delete this rule permanently? This action cannot be undone.',
                              variant: 'danger',
                              confirmText: 'Delete',
                              cancelText: 'Cancel'
                            });
                            if (confirmed) {
                              const deleteId = ruleId || rule?.id || data.id;
                              if (deleteId) {
                                await onDelete(deleteId);
                              }
                            }
                          }}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-error/10 text-error flex items-center gap-2"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Offense selection area - similar to ZeroToleranceRuleForm */}
          <div className="mt-3 border border-base-200 rounded-lg p-3 min-h-[80px] bg-base-50">
            <div className="flex flex-wrap gap-2 mb-3">
                {getSelectedOffenses().map((offense) => (
                    <div key={offense.id} className="relative group">
                      <OffenceCard
                        offence={offense}
                        category={offense.category}
                        variant="button"
                        showLevel={false}
                        className="relative"
                      />
                      <button
                        onClick={() => removeOffense(offense.id)}
                        className="absolute -top-1 -right-1 bg-base-200 text-base-content rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-base-300 shadow-sm"
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
              </div>

              {/* Add offense button */}
              <div className="flex justify-center">
                <div className="relative" ref={offenseSelectorRef}>
                  <button
                    onClick={() => setShowOffenseSelector(!showOffenseSelector)}
                    className="px-3 py-2 text-base-content/60 rounded-lg text-sm hover:bg-base-100 hover:text-base-content transition-colors flex items-center gap-1.5"
                  >
                    <PlusIcon className="w-4 h-4" />
                    {isRecognitionMode ? 'Add behaviour' : 'Add offense'}
                  </button>

                  {/* Offense selector dropdown */}
                  <AnimatePresence>
                    {showOffenseSelector && availableOffensesFiltered.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        className="absolute left-1/2 transform -translate-x-1/2 mt-1 w-80 max-h-64 overflow-y-auto bg-white border border-base-200 rounded-lg shadow-lg z-10"
                      >
                        <div className="p-2 space-y-1">
                          {availableOffensesFiltered.map(offense => (
                            <OffenceCard
                              key={offense.id}
                              offence={offense}
                              category={offense.category}
                              variant="list"
                              showLevel={true}
                              showDescription={true}
                              clickable={true}
                              onClick={() => addOffense(offense)}
                              className="hover:bg-base-50"
                            />
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* Contract duration and consequence */}
            <div className="text-base mt-3">
              {isRecognitionMode ? 'Goal duration:' : 'Contract duration:'}{' '}
              <EditableField
                field="duration"
                value={data.duration}
                type="number"
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
                validation={{ min: 1, max: 999 }}
              />
              {' '}
              <EditableField
                field="durationType"
                value={data.durationType}
                options={DURATION_TYPE_OPTIONS}
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
              />
              . {isRecognitionMode ? 'Success earns' : 'Violation results in'}{' '}
              <EditableField
                field="violationConsequence"
                value={data.violationConsequence}
                options={consequenceOptions}
                onUpdate={update}
                editingField={editingField}
                setEditingField={setEditingField}
              />
              .
          </div>

          <AnimatePresence mode="wait">
            {showConfig && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15, ease: "easeInOut" }}
                style={{ overflow: 'hidden' }}
              >
                <div className="mt-4 pt-4 border-t border-base-200 space-y-4">
                  <MultiSelectDropdown
                    label="APPLIES TO"
                    value={data.targets}
                    options={targetOptions}
                    onChange={(newTargets) => update({ targets: newTargets })}
                    placeholder="Select students or grades"
                  />

                  {/* Note: Offense selection is now in the main view above with visual cards */}

                  {/* Date section */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-base-content/70 mb-1 block">START DATE</label>
                      <input
                        ref={datePickerRef}
                        type="date"
                        value={data.startDate}
                        onChange={(e) => {
                          update({ startDate: e.target.value });
                          // Force the picker to close
                          setTimeout(() => {
                            if (datePickerRef.current) {
                              datePickerRef.current.blur();
                            }
                          }, 100);
                        }}
                        className="input input-bordered input-sm w-full"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-base-content/70 mb-1 block">END DATE</label>
                      <div className="input input-bordered input-sm w-full bg-base-100 text-base-content/70 flex items-center">
                        {formatDateForDisplay(data.endDate)}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default BehaviorContractForm;